import { useState, useEffect } from "react";
import Select from "react-select";
import { motion } from "framer-motion";
import { ArrowRight, Plus, Filter, RotateCcw } from "lucide-react";
import DataFetcherOfertaPosgrados from "../../hooks/DataFetcherOfertaPosgrados";
import Campo from "../posgrados/Campo";

const facultadSlugMap = {
  empresas: "Ciencias Económicas y Empresariales",
  exactas: "Ciencias Exactas y Naturales",
  salud: "Ciencias de la Salud",
  sociales: "Ciencias Sociales, Educación y Humanidades",
  juridicas: "Ciencias Jurídicas y Políticas",
  ingenieria: "Ingenierías y Arquitectura",
  negocios: "UTPL Escuela de Negocios",
};

const facultadNameToSlug = Object.fromEntries(
  Object.entries(facultadSlugMap).map(([slug, name]) => [name, slug]),
);

function OfferPosgrados() {
  const optionsPrograma = [
    { value: "todos", label: "Todos los programas" },
    { value: "Maestría", label: "Maestría" },
    { value: "Especialización", label: "Especialización" },
    { value: "Doctorado", label: "Doctorado" },
  ];

  const optionsFacultad = [
    { value: "todos", label: "Toda la oferta" },
    {
      value: "Ciencias Económicas y Empresariales",
      label: "Ciencias Económicas y Empresariales",
    },
    {
      value: "Ciencias Exactas y Naturales",
      label: "Ciencias Exactas y Naturales",
    },
    { value: "Ciencias de la Salud", label: "Ciencias de la Salud" },
    {
      value: "Ciencias Sociales, Educación y Humanidades",
      label: "Ciencias Sociales, Educación y Humanidades",
    },
    {
      value: "Ciencias Jurídicas y Políticas",
      label: "Ciencias Jurídicas y Políticas",
    },
    {
      value: "Ingenierías y Arquitectura",
      label: "Ingenierías y Arquitectura",
    },
    { value: "UTPL Escuela de Negocios", label: "UTPL Escuela de Negocios" },
  ];

  const optionsModalidad = [
    { value: "todos", label: "Todas las modalidades" },
    { value: "En línea", label: "En línea" },
    { value: "Presencial", label: "Presencial" },
    { value: "Híbrida", label: "Híbrida" },
  ];

  const getInitialValue = (param, options) => {
    const params = new URLSearchParams(window.location.search);
    let value = params.get(param);

    if (param === "facultad" && facultadSlugMap[value]) {
      value = facultadSlugMap[value];
    }

    return options.find((o) => o.value === value) || options[0];
  };

  const [programaSeleccionado, setProgramaSeleccionado] = useState(
    getInitialValue("programa", optionsPrograma),
  );

  const [facultadSeleccionada, setFacultadSeleccionada] = useState(
    getInitialValue("facultad", optionsFacultad),
  );

  const [modalidadSeleccionada, setModalidadSeleccionada] = useState(
    getInitialValue("modalidad", optionsModalidad),
  );

  useEffect(() => {
    const params = new URLSearchParams();

    if (programaSeleccionado.value !== "todos")
      params.set("programa", programaSeleccionado.value);

    if (facultadSeleccionada.value !== "todos") {
      const slug = facultadNameToSlug[facultadSeleccionada.value];
      params.set("facultad", slug);
    }

    if (modalidadSeleccionada.value !== "todos")
      params.set("modalidad", modalidadSeleccionada.value);

    const newUrl =
      window.location.pathname +
      (params.toString() ? `?${params.toString()}` : "");

    window.history.replaceState({}, "", newUrl);
  }, [programaSeleccionado, facultadSeleccionada, modalidadSeleccionada]);

  const limpiarFiltros = () => {
    setProgramaSeleccionado(optionsPrograma[0]);
    setFacultadSeleccionada(optionsFacultad[0]);
    setModalidadSeleccionada(optionsModalidad[0]);
  };

  const DataDisplayOfertaPosgrados = ({ data }) => {
    const cleanText = (text) => {
      if (!text) return "";
      return text
        .toLowerCase()
        .trim()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");
    };

    // Diccionario de colores según el tipo de programa
    const badgeColors = {
      Maestría: "bg-blue-100 text-blue-800 border border-blue-200",
      Especialización: "bg-purple-100 text-purple-800 border border-purple-200",
      Doctorado: "bg-emerald-100 text-emerald-800 border border-emerald-200",
    };

    // 1. FILTRAR los datos
    const filtered = data.filter((item) => {
      if (
        programaSeleccionado.value !== "todos" &&
        item.field_programa !== programaSeleccionado.value
      ) {
        return false;
      }
      if (
        facultadSeleccionada.value !== "todos" &&
        item.field_facultad !== facultadSeleccionada.value
      ) {
        return false;
      }
      if (
        modalidadSeleccionada.value !== "todos" &&
        item.field_modalidad !== modalidadSeleccionada.value
      ) {
        return false;
      }
      return true;
    });

    // 2. ORDENAR los datos (Fecha DESC, Título ASC)
    const filteredData = [...filtered].sort((a, b) => {
      const dateA = a.field_inicio_clases || "";
      const dateB = b.field_inicio_clases || "";

      const dateComparison = dateB.localeCompare(dateA, "es");
      if (dateComparison !== 0) {
        return dateComparison;
      }

      const titleA = cleanText(a.title);
      const titleB = cleanText(b.title);
      return titleA.localeCompare(titleB, "es");
    });

    return (
      <div className="px-16 md:px-10">
        {/* Contenedor de Filtros */}
        <div className="wrapper-filtros layout9">
          <div>
            <p>
              <b>Programa</b>
            </p>
            <Select
              options={optionsPrograma}
              value={programaSeleccionado}
              onChange={setProgramaSeleccionado}
            />
          </div>

          <div>
            <p>
              <b>Facultad</b>
            </p>
            <Select
              options={optionsFacultad}
              value={facultadSeleccionada}
              onChange={setFacultadSeleccionada}
            />
          </div>

          <div>
            <p>
              <b>Modalidad</b>
            </p>
            <Select
              options={optionsModalidad}
              value={modalidadSeleccionada}
              onChange={setModalidadSeleccionada}
            />
          </div>

          <div>
            <button
              className="btn-reset-filtros flex items-center gap-2"
              onClick={limpiarFiltros}
            >
              <RotateCcw size={16} />
              Limpiar filtros
            </button>
          </div>
        </div>

        {/* Contador de resultados */}
        <div className="mb-6 flex items-center justify-between">
          <p className="text-gray-700 flex items-center gap-2">
            <Filter size={16} />
            <span className="font-bold">{filteredData.length}</span> programas
            encontrados
          </p>
        </div>

        {/* Grid de Tarjetas */}
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {filteredData.map((item) => (
            <motion.a
              key={item.id}
              href={item.field_url}
              rel="noreferrer"
              className="group relative block bg-white rounded-2xl shadow-md overflow-hidden"
              initial="rest"
              animate="rest"
              whileHover="hover"
            >
              <div className="h-40 overflow-hidden">
                <img
                  src={item.field_imagen_portada}
                  alt={item.title}
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                />
              </div>

              <motion.div
                variants={{
                  rest: { scale: 1, opacity: 0.8 },
                  hover: { scale: 1.2, opacity: 1 },
                }}
                className="absolute top-4 right-4 bg-white/90 backdrop-blur-md rounded-full p-2 shadow-md"
              >
                <Plus size={12} />
              </motion.div>

              <div className="p-5 space-y-3">
                <div className="flex gap-2 flex-wrap">
                  {/* Aquí asignamos dinámicamente las clases de color según el tipo de programa */}
                  <span
                    className={`text-xs font-semibold px-2.5 py-1 rounded-md ${badgeColors[item.field_programa] || "bg-gray-100 text-gray-800"}`}
                  >
                    {item.field_programa}
                  </span>
                </div>

                <h3 className="text-base font-bold leading-tight p-0">
                  {item.title}
                </h3>

                {item.field_modalidad && (
                  <p className="text-sm text-gray-600 p-0 leading-none">
                    <strong>Modalidad:</strong> {item.field_modalidad}
                  </p>
                )}

                {item.field_duracion && (
                  <p className="text-sm text-gray-600 p-0 leading-none">
                    <strong>Duración:</strong>{" "}
                    {item.field_duracion
                      .replaceAll("_", " ")
                      .replace("anos", "años")
                      .replace("ano", "año")}
                  </p>
                )}

                {item.field_facultad && (
                  <p className="text-sm text-gray-600 p-0 leading-none">
                    <strong>Facultad:</strong> {item.field_facultad}
                  </p>
                )}

                {item.field_campo_amplio && (
                  <p className="text-sm text-gray-600 p-0 leading-none">
                    <strong>Campo: </strong>
                    <Campo value={item.field_campo_amplio} />
                  </p>
                )}

                {item.field_inicio_clases && (
                  <p className="text-sm text-gray-600 p-0 leading-none">
                    <strong>Inicio de clases:</strong>{" "}
                    {item.field_inicio_clases}
                  </p>
                )}
              </div>

              <motion.div
                variants={{
                  rest: { opacity: 0 },
                  hover: { opacity: 1 },
                }}
                className="absolute inset-0 bg-black/10 flex items-center justify-center"
              >
                <ArrowRight size={44} className="text-white" />
              </motion.div>
            </motion.a>
          ))}
        </div>
      </div>
    );
  };

  return (
    <DataFetcherOfertaPosgrados>
      {(data) => <DataDisplayOfertaPosgrados data={data} />}
    </DataFetcherOfertaPosgrados>
  );
}

export default OfferPosgrados;
